import { useUserStore } from '@bv/stores';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

import BillingApi, { Module } from '@/api/billing/billing.api';
import type { WorkspaceQuotaStatusData } from '@/types/billing/workspace-quota-status.types';

export { Module } from '@/api/billing/billing.api';

export interface WorkspaceBillingQuota {
  quota: Ref<WorkspaceQuotaStatusData | null>;
  isFetching: Ref<boolean>;
  fetchAttempted: Ref<boolean>;
  percentage: ComputedRef<number>;
  remaining: ComputedRef<number>;
  BillingStartDate: ComputedRef<string>;
  isQuotaFull: ComputedRef<boolean>;
  isQuotaExpired: ComputedRef<boolean>;
  showWarning80: ComputedRef<boolean>;
  showLimit100: ComputedRef<boolean>;
  setQuota: (data: WorkspaceQuotaStatusData) => void;
  fetchQuota: () => Promise<void>;
}

function createWorkspaceBillingQuota(module: Module): WorkspaceBillingQuota {
  const quota = ref<WorkspaceQuotaStatusData | null>(null);
  const isFetching = ref(false);
  const fetchAttempted = ref(false);

  const percentage = computed(() => quota.value?.percentage ?? 0);
  const remaining = computed(() => quota.value?.remaining ?? 0);
  const BillingStartDate = computed(() => quota.value?.BillingStartDate ?? '');

  const isQuotaFull = computed(() => {
    const q = quota.value;
    if (!q) {
      return false;
    }
    return q.percentage >= 100 || q.remaining <= 0;
  });

  const showWarning80 = computed(() => {
    const q = quota.value;
    if (!q || q.percentage >= 100 || q.remaining <= 0) {
      return false;
    }
    return q.percentage >= 80;
  });

  const showLimit100 = computed(() => {
    const q = quota.value;
    if (!q) {
      return false;
    }
    return q.percentage >= 100 || q.remaining <= 0;
  });

  const isQuotaExpired = computed(() => {
    const q = quota.value;
    if (!q) {
      return false;
    }
    return q.percentage === 100 && q.remaining === 0;
  });

  function setQuota(data: WorkspaceQuotaStatusData): void {
    if (useUserStore().currentService?.billing_mode !== 'zoho') {
      return;
    }
    quota.value = data;
  }

  async function fetchQuota(): Promise<void> {
    if (useUserStore().currentService?.billing_mode !== 'zoho') {
      quota.value = null;
      fetchAttempted.value = true;
      return;
    }
    isFetching.value = true;
    try {
      const data = await BillingApi.getModuleQuotaStatus(module);
      setQuota(data);
    } finally {
      isFetching.value = false;
      fetchAttempted.value = true;
    }
  }

  return {
    quota,
    isFetching,
    fetchAttempted,
    percentage,
    remaining,
    BillingStartDate,
    isQuotaFull,
    isQuotaExpired,
    showWarning80,
    showLimit100,
    setQuota,
    fetchQuota,
  };
}

const quotaByModule = new Map<Module, WorkspaceBillingQuota>();

/**
 * Shared workspace billing quota for a billing `Module` (`broadcast`, `mac`, …).
 * Same module returns the same reactive instance (layout, routes, and socket handlers stay in sync).
 */
export function useWorkspaceBillingQuota(module: Module): WorkspaceBillingQuota {
  let entry = quotaByModule.get(module);
  if (!entry) {
    entry = createWorkspaceBillingQuota(module);
    quotaByModule.set(module, entry);
  }
  return entry;
}
